import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import './DialogueSelectForm.pcss';
import { Button, Heading } from '@mm/components/src';
import { EditIcon, PathIcon } from '@mm/icons/general';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { DialogueNameForm } from './DialogueNameForm/DialogueNameForm';

type Label = string | Element | JSX.Element;

type Props = {
  className?: string;
  dialogueId?: string | number;
  dialogueName?: string;
  label?: Label;
  disabled?: boolean;
  canRename?: boolean;
  selectButtonLabel?: Label;
  onCreateClick?: () => void;
  onChangeClick?: () => void;
};

const ignoreList = [
  'welcome_page',
  'default_page',
  'unsubscribe_page',
  'onboarding_welcome',
  'wp_answering_service',
  'away_responder',
  'landing_page',
  'otn_page',
  'sms_reply_page',
  'instagram_welcomer',
];

export const DialogueSelectForm = ({
  className,
  disabled,
  canRename,
  dialogueId,
  dialogueName,
  label = 'Selected dialogue: ',
  selectButtonLabel = 'Change Dialogue',
  onCreateClick = () => {},
  onChangeClick = () => {},
}: Props) => {
  const pages = useSelector((state: StoreState) => state.page.entities);
  const page = pages.find(p => p.id === dialogueId);

  const [mode, setMode] = useState<'' | 'edit'>('');

  const showRename = useMemo(() => {
    return canRename || !!(page?.id && !ignoreList.includes(page?.kind));
  }, [page, canRename]);

  const cn = useMemo(
    () => ({
      container: classnames(className, 'dialogue-select-form', {
        'dialogue-select-form--disabled': disabled,
      }),
    }),
    [],
  );

  const nameFormatted = useMemo(() => {
    return dialogueName || page?.name || `Dialogue id: ${dialogueId}`;
  }, [dialogueName, page, dialogueId]);

  const onEditClick = useCallback(() => {
    setMode('edit');
  }, []);

  const closeEdit = useCallback(() => {
    setMode('');
  }, []);

  return (
    <div className={cn.container}>
      {dialogueId && (
        <div className="dialogue-select-form__selected">
          <div className="dialogue-select-form__label">{label}</div>

          {!mode && (
            <Heading size={5} className="dialogue-select-form__name">
              {nameFormatted}

              <Button
                css={{ container: 'dialogue-select-form__dialogue-btn' }}
                color="secondary-blue"
                size="sm"
                disabled={disabled}
                onClick={onChangeClick}
              >
                <PathIcon />
                {selectButtonLabel}
              </Button>

              {showRename && (
                <span
                  className="dialogue-select-form__icon"
                  onClick={onEditClick}
                >
                  <EditIcon />
                </span>
              )}
            </Heading>
          )}

          {mode === 'edit' && page && (
            <DialogueNameForm
              dialogueId={dialogueId}
              botId={page.botId}
              name={nameFormatted}
              onCancel={closeEdit}
              onSuccess={closeEdit}
              onError={closeEdit}
            />
          )}
        </div>
      )}

      {!dialogueId && (
        <div className="dialogue-select-form__empty">
          <div className="dialogue-select-form__label">
            Take users to a new Dialogue?
          </div>
          <Button
            css={{
              container: 'dialogue-select-form__dialogue-btn',
            }}
            color="secondary-blue"
            size="lg"
            disabled={disabled}
            onClick={onCreateClick}
          >
            <PathIcon />
            Choose / Create Dialogue
          </Button>
        </div>
      )}
    </div>
  );
};
